import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon99Minutos from '@/assets/deliveryTypes/Icon99Minutos';
import NationalIcon from '@/assets/deliveryTypes/NationalIcon';
import Icon99MinutosGrey from '@/assets/services/Icon99MinutosGrey';
import IconCoo2Fre from '@/assets/services/IconCoo2Fre';
import IconRetorno from '@/assets/services/IconRetorno';
import IconSameDay from '@/assets/services/IconSameDay';
import IconSprint from '@/assets/services/IconSprint';
import { RootState } from '@/redux/store';

import type { DeliveryTypes, Services } from '@/types/parcel/userData/profile.interface';

import { creditServicesbyCountry, links, servicesByCountry, traslateLabels } from './dependencies';

interface ServiceItems {
  value: boolean | number;
  link: string;
  label: string;
}

const useServices = () => {
  const { profile } = useSelector((state: RootState) => state.parcel);
  const [availableServices, setAvailableServices] = useState<ServiceItems[]>([]);
  const [unavailableServices, setUnavailableServices] = useState<ServiceItems[]>([]);
  const configuration = profile?.data?.configuration;
  const services = configuration?.services;
  const deliveryTypes = configuration?.deliveryTypes;
  const allServices: { [key: string]: { value: boolean | number; link: string; label: string } } =
    {};
  const hideServices = ['hand2Hand', 'ecoTag'];

  useEffect(() => {
    if (profile.data && !profile.loading) {
      const isCreditAccount = profile.data?.configuration?.paymentType == 'CREDIT';
      Object.keys(links)?.forEach((key) => {
        if (servicesByCountry[profile.data?.country!]?.includes(key)) {
          if (key === 'cashOnDelivery' || key === 'insurance') {
            allServices[key] = {
              value: !!services?.[key].enabled,
              link: links?.[key],
              label: traslateLabels?.[key],
            };
          } else {
            allServices[key] = {
              value: isCreditAccount
                ? Boolean(
                    services?.[key as keyof Services] ||
                    deliveryTypes?.[key as keyof DeliveryTypes],
                  )
                : profile.data?.country &&
                    creditServicesbyCountry[
                      profile.data.country as keyof typeof creditServicesbyCountry
                    ]?.includes(key)
                  ? hideServices.push(key)
                  : Boolean(
                      services?.[key as keyof Services] ||
                      deliveryTypes?.[key as keyof DeliveryTypes],
                    ),
              link: links?.[key as keyof typeof links],
              label: traslateLabels?.[key as keyof typeof traslateLabels],
            };
          }
        }
      });
    }
  }, [profile]);

  useEffect(() => {
    if (Object.keys(allServices).length > 0) {
      setAvailablesAndUnavailablesSv();
    }
  }, [allServices]);

  const setAvailablesAndUnavailablesSv = () => {
    const availables: ServiceItems[] = [];
    const unavailables: ServiceItems[] = [];
    Object.keys(allServices)?.forEach((key) => {
      if (Boolean(allServices?.[key]?.value) === true && !hideServices.includes(key)) {
        availables.push(allServices?.[key]);
      }
    });

    Object.keys(allServices)?.forEach((key) => {
      if (Boolean(allServices?.[key]?.value) === false && !hideServices.includes(key)) {
        unavailables.push(allServices?.[key]);
      }
    });
    if (availables.length > 0) {
      setAvailableServices(availables);
    }
    if (unavailables.length > 0) {
      setUnavailableServices(unavailables);
    }
  };

  const iconProducts = (isDisabled: boolean, label: string): React.ReactElement => {
    if (isDisabled) {
      return <Icon99MinutosGrey sx={{ width: '1.5rem', height: '1.5rem' }} />;
    }
    switch (label) {
      case 'Same Day':
        return <IconSameDay />;
      case 'Sprint':
        return <IconSprint />;
      case 'CO2 Free':
        return <IconCoo2Fre />;
      case 'Retorno':
        return <IconRetorno />;
      case 'Nacional':
        return <NationalIcon sx={{ width: '1.5rem', height: '1.5rem' }} />;
      case '99minutos':
        return <Icon99Minutos sx={{ width: '1.5rem', height: '1.5rem' }} />;
      default:
        return <Icon99Minutos sx={{ width: '1.5rem', height: '1.5rem' }} />;
    }
  };

  return {
    unavailableServices,
    availableServices,
    loading: profile.loading,
    country: profile.data?.country,
    iconProducts,
  };
};

export default useServices;
